/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { 
  Box, 
  Select, 
  styled, 
  TextField, 
  OutlinedInput, 
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import ReactFlagsSelect from "react-flags-select";
import PhoneInput from "react-phone-input-2";

const disabledFieldStyles = {
  "&.Mui-disabled, & .MuiInputBase-root.Mui-disabled": {
    backgroundColor: "#f5f7fa",
    cursor: "not-allowed",
  },
  "&.Mui-disabled .MuiInputBase-input, & .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#637084",
    opacity: 1,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    transition: "border-color 0.2s ease, background-color 0.2s ease",
  },
  "&.Mui-disabled .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "#d9dee7",
  },
};

export const StyledTextField = styled(TextField)(({ error }) => ({
  width: "50%",
  height: "50px",
  borderRadius: "8px",
  borderWidth: "0.25px",
  gap: "4px",

  ".MuiInputBase-root": {
    height: "100%",
  },
  '&:hover': {
    "& .MuiOutlinedInput-notchedOutline": {
      border: '2px solid #395bbd !important'
    },
  },

  '&:focus-within': {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: error ? "#d32f2f" : "#1976d2 !important",
      borderRadius: "8px",
    }
  },

  "& .MuiInputBase-root": {
    "&.MuiInputBase-root fieldset": {
      borderColor: error ? "#d32f2f" : "#D8D9DB",
      borderRadius: "8px",
    },
  },
  ...disabledFieldStyles,
}));

export const StyledDescriptionField = styled(TextField)({
  '&:hover': {
    "& .MuiOutlinedInput-notchedOutline": {
      border: '2px solid #395bbd !important'
    }
  },
  width: "50%",
  height: "50px",
  borderRadius: "8px",
  borderWidth: "0.25px",
  gap: "4px",

  ".MuiInputBase-root": {
    height: "100%",
  },

  "& .MuiInputBase-root": {
    "&.MuiInputBase-root fieldset": {
      borderRadius: "8px",
    },
  },
})

export const StyledSelect = styled(Select)({
  width: "100%",
  height: "50px",
  borderRadius: "8px",
  borderWidth: "0.25px",
  gap: "4px",

  "&.MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#D8D9DB",
    },
    "&:hover fieldset": {
      borderColor: "#395bbd",
      borderWidth: '2px'
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1976d2",
    },
  },
});

export const FieldContainer = styled(Box)({
  marginBottom: "16px",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

export const HeaderText = styled("p")({
  fontSize: "18px",
  fontWeight: "600",
});

export const ConfigurationContainer = styled('div')({
  padding: '1.75vh 9.5vw',
})

export const StyledCredentialField = styled(OutlinedInput)(({ error }) => ({
  width: "50%",
  height: "50px",
  borderRadius: "8px",
  borderWidth: "0.25px",
  gap: "4px",
  ".MuiInputBase-root": {
    height: "100%",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: error ? "#d32f2f" : "#D8D9DB",
    borderRadius: "8px",
  },
  '&:hover': {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: error ? "#d32f2f" : "#395bbd",
      borderRadius: "8px",
      borderWidth: '2px',
    }
  },
  '&:focus-within': {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: error ? "#d32f2f" : "#1976d2",
      borderRadius: "8px",
    }
  },
  ...disabledFieldStyles,
}));

export const StyledTestConnectionButton = styled(Button)({
  fontWeight: '400',
  '&:hover': {
    borderColor: '#395bbd',
    color: '1976D2',
  },
  marginTop: '-0.5vh',
  textTransform: 'none',
  borderColor: '#D8D9DB',
  color: '#1976D2',
  width: 'fit-content',
  minWidth: '200px',
  height: '40px',
  paddingLeft: '16px',
  paddingRight: '16px',
  whiteSpace: 'nowrap',
})

export const StyledHelperText = styled('span')({
  color: '#d32f2f',
  fontSize: '0.75rem',
  padding: '12px !important',
  letterSpacing: "0.3px"
})

export const LocationContainer = styled('div')({
  padding: '1.75vh 9.5vw',
})

export const StyledCountrySelect = styled(ReactFlagsSelect)({
   "& .flag-select": {
    boxShadow: '0px 1px 4px 0px #00000026 !important',
    borderRadius: '8px !important',
    height: '50px !important',
    border: '0.25px solid transparent !important',
    display: 'flex !important',
    alignItems: 'center !important',
    padding: '0 12px !important',
  }
})

export const StyledParagraphField = styled(TextField)({
  "& .MuiInputBase-root fieldset": {
    border: '1px solid transparent',
    "&:hover": {
      border: '1px solid transparent'
    }
  },
  '& .MuiOutlinedInput-root': {
    '&:hover': {
     border: '1px solid #D8D9DB',
    },
    border: '1px solid #D8D9DB',
    borderRadius: '8px',
  },
  '&:hover': {
    "& .MuiOutlinedInput-notchedOutline": {
      border: '1px solid transparent !important'
    },
  },
  '&:focus-within': {
    "& .MuiOutlinedInput-notchedOutline": {
      border: '1px solid #D8D9DB !important'
    },
  },
})

export const StyledFlagsWrapper = styled("div")(() => ({
  '&:hover':{
    '[class*="ReactFlagsSelect-module_selectBtn"]':{
      outline: "2px solid #395bbd !important",
      outlineOffset: "-1px",
    }
  },
  '&:focus-within': {
    '[class*="ReactFlagsSelect-module_selectBtn"]':{
      outline: "2px solid #1976d2 !important",
      outlineOffset: "-1px",
    }
  },
  width: "50%",
  height: "50px",
  borderRadius: "8px",
  fontSize: "16px",
  display: "flex",
  alignItems: "center",

  ".flag-select": {
    width: "100%",
    height: "100%",
  },

  '[class*="ReactFlagsSelect-module_selectBtn"]': {
    width: "100%",
    height: "100%",
    padding: "0 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "16px",
    fontFamily: "inherit",
    borderRadius: "8px",
    backgroundColor: "white",
    border: "1px solid #D8D9DB",
    boxShadow: "none",
    outline: "none",
  },
}))

export const ImageContainer = styled(Box)({
  display: "flex",
  justifyContent: "flex-start",
  gap: "8px",
  alignItems: "center",
  marginTop: '-10px',
})

export const StyledInput = styled("input")({
  opacity: 0,
  position: "absolute",
  left: "0",
  top: "0",
  width: "100%",
  height: "100%",
  cursor: "pointer",
});

export const ColorContainer = styled(Box)({
  '&:hover':{
    outline: "2px solid #395bbd",
    outlineOffset: "-1px",
  },
  '&:focus-within':{
    outline: "2px solid #1976d2",
    outlineOffset: "-1px",
  },
  width: "50%",
  height: "50px",
  border: "1px solid #D8D9DB",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginBottom: "16px",
})

export const SubColorContainer = styled(Box)({
  height: "inherit",
  width: "10%",
  borderRadius: "8px 0px 0px 8px",
  marginLeft: "-0.6px",
  borderLeft: "inherit",
});

export const ThumbnailPreviewContainer = styled('div')({
  border: "0.25px solid transparent",
  boxShadow: '0px 1px 4px 0px #00000026',
  width: '355px', 
  height: 200, 
  maxHeight: 200,
  borderRadius: "8px",
  position: 'relative',
})

const APPLE_WALLET_FIELD_WIDTH = 355;
const APPLE_WALLET_STRIP_ASPECT_RATIO = 750 / 246;
const APPLE_WALLET_STRIP_HEIGHT = Math.round(APPLE_WALLET_FIELD_WIDTH / APPLE_WALLET_STRIP_ASPECT_RATIO);

export const AppleWalletStripFrame = styled('div')(({ bgColor = 'transparent' }) => ({
  width: `${APPLE_WALLET_FIELD_WIDTH}px`,
  height: `${APPLE_WALLET_STRIP_HEIGHT}px`,
  borderRadius: '8px',
  position: 'relative',
  backgroundColor: bgColor,
}))

export const AppleWalletStripSurface = styled('div', {
  shouldForwardProp: (prop) => prop !== 'bgColor',
})(({ bgColor = 'transparent' }) => ({
  width: '100%',
  height: '100%',
  borderRadius: '8px',
  boxShadow: '0px 1px 4px 0px #00000026',
  overflow: 'hidden',
  backgroundColor: bgColor,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const AppleWalletStripLogoContainer = styled('div')({
  width: '100%',
  height: '75%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
});

export const AppleWalletStripLogoImage = styled('img')({
  width: 'auto',
  height: '100%',
  display: 'block',
  objectFit: 'contain',
});

export const ThumbnailContainer = styled('div')({
  '@media (max-width: 1500px)': {
    flexDirection: 'column !important',
  },
  display: 'flex'
})

export const StyledIconButton = styled(IconButton)({
  padding: '4px',
  cursor: 'pointer',
})

export const StyledColorField = styled(TextField)(({ error }) => ({
  width: "100%",
  height: "40px",
  borderRadius: "8px",
  borderWidth: "0.25px",

  ".MuiInputBase-root": {
    height: "100%",
  },

  "& .MuiInputBase-root": {
    "&.MuiInputBase-root fieldset": {
      borderColor: error ? "#d32f2f" : "transparent",
      borderRadius: "8px",
    },
  },
}))

export const StyledSectionTitle = styled(Typography)({
  marginTop: '20px',
  fontSize: '24px',
  lineHeight: '40px',
  letterSpacing: '0.15%',
  fontWeight: '600',
})

export const StyledSectionSubTitle = styled(Typography)({
  color: 'black',
  fontSize: '16px',
  fontWeight: '400',
  marginBottom: '1px',
})

export const StyledLocationTextField = styled(TextField)(({ error }) => ({
  width: "100%",
  height: "50px",
  borderRadius: "8px",
  borderWidth: "0.25px",
  gap: "4px",
  '&:hover': {
    "& .MuiOutlinedInput-notchedOutline": {
      border: '2px solid #395bbd !important'
    },
  },

  '&:focus-within': {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: error ? "#d32f2f" : "#1976d2 !important",
      borderRadius: "8px",
    }
  },

  ".MuiInputBase-root": {
    height: "100%",
  },

  "& .MuiInputBase-root": {
    "&.MuiInputBase-root fieldset": {
      borderColor: error ? "#d32f2f" : "#D8D9DB",
      borderRadius: "8px",
    },
  },
}));

export const LocationCoordinateContainer = styled('div')({
  display: 'flex', 
  justifyContent: 'space-between', 
  marginTop: '2vh', 
  gap: '10px'
})

export const StyledPhoneInput = styled(PhoneInput)({
  '&:hover':{
    outline: "2px solid #395bbd !important",
    outlineOffset: "-1px",
  },
  '&:focus-within':{
    outline: "2px solid #1976d2 !important",
    outlineOffset: "-1px",
  },
  height: "50px",
  borderRadius: "8px",
  borderWidth: "0.25px",
  gap: "4px",
})

export const PhotoUploadButton = styled(Button)({
  '&:hover':{
    border: "1px solid #395bbd !important",
  },
  marginBottom: "20px",
  textTransform: "none",
  width: "167px",
  height: "50px",
  border: "1px solid #CED4DA",
  padding: '10px',
})

export const AddAnotherButton = styled(Button)({
  '&:hover':{
    border: "1px solid #395bbd !important",
  },
  marginBottom: "20px",
  textTransform: "none",
  width: "147px",
  height: "40px",
  border: "1px solid #CED4DA",
})

export const StyledBannerSubTitle = styled(Typography)({
  color: 'black',
  fontSize: '16px',
  fontWeight: '400',
  margin: '20px 0px 10px 0px',
})

export const BannerSectionContainer = styled('div')({
  width: '44.5vw',
})

export const BannerUploadContainer = styled('div')({
  display: 'flex',
  gap: '30px',
})

export const UploadedImage = styled('img')({
  width: "355px",
  height: 200,
  objectFit: "cover",
  borderRadius: '8px',
})

export const UploadButtonContainer = styled('div')({
  marginTop: '10px',
})

export const StyledLabel = styled('label')({
  color: "black",
  cursor: "pointer",
  fontWeight: '400',
})

export const StyledUploadInput = styled('input')({
  display: "none",
})

export const StyledImageRequirementTitle = styled(Typography)({
  color: 'black',
  fontSize: '16px',
  fontWeight: '400',
  padding: 0,
  margin: 0,
})

export const StyledListItem = styled('li')({
  color: '#6F6F6F',
})

export const BannerPreviewTitle = styled(Typography)({
  color: 'black',
  fontSize: '16px',
  fontWeight: '400',
  marginTop: '30px',
  textAlign: 'left',
})

export const PreviewImage = styled('img')({
  objectFit: "cover",
  width: "100%",
  height: "100%",
  position: 'absolute',
  border: '0.25px solid transparent',
  borderRadius: '8px',
})

export const colorContainerStyles = {
  position: "relative",
  display: "inline-block",
  margin: '20px 0px 10px 0px',
}

export const StyledButton = styled(Button)({
  textTransform: 'none',
  height: '48px',
  width: '120px',
})

export const UploadText = styled(Typography)({
  marginTop: '12px !important',
})

export const BusinessHourRangeText = styled('div')({
  color: "black",
  fontSize: "16px",
  fontWeight: "400",
  paddingTop: '10px',
})

export const StyledUploadButtonText = styled('span')({
  color: "black",
  cursor: "pointer",
  fontWeight: '400',
})
